import React, { useEffect, useRef } from "react";

/**
 * Lightweight accessible Lightbox
 * Props:
 * - open (bool)
 * - images (array of urls)
 * - index (number)
 * - title (string)
 * - onClose, onPrev, onNext (functions)
 *
 * Keyboard: Esc closes, Left/Right navigates
 * Focus: close button is focused when opened
 */
export default function LightboxModal({ open, images = [], index = 0, title = "", onClose = () => {}, onPrev = () => {}, onNext = () => {} }) {
  const closeRef = useRef(null);

  useEffect(() => {
    if (!open) return;
    const onKey = (e) => {
      if (e.key === "Escape") onClose();
      if (e.key === "ArrowLeft") onPrev();
      if (e.key === "ArrowRight") onNext();
    };
    document.addEventListener("keydown", onKey);
    // focus close button for screen reader users
    setTimeout(() => closeRef.current?.focus(), 10);
    return () => document.removeEventListener("keydown", onKey);
  }, [open, onClose, onPrev, onNext]);

  if (!open) return null;

  const currentUrl = images[index] || "";

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-black/85" onClick={onClose} />
      <div className="relative z-10 max-w-[95vw] max-h-[95vh] w-full">
        <div className="flex items-center justify-between mb-3 text-white">
          <h3 className="font-inria text-lg">{title}</h3>
          <div className="flex items-center gap-2">
            <button ref={closeRef} onClick={onClose} className="text-white/90 bg-white/5 px-3 py-1 rounded focus:outline-none focus:ring-2 focus:ring-weddmac-gold" aria-label="Close gallery">
              Close
            </button>
          </div>
        </div>

        <div className="bg-black/95 rounded-lg overflow-hidden flex items-center justify-center">
          <button aria-label="Previous" onClick={onPrev} className="hidden md:flex items-center justify-center w-12 h-12 text-white/80 hover:text-white">‹</button>
          <img src={currentUrl} alt={`${title} ${index + 1}`} className="max-w-full max-h-[80vh] object-contain" decoding="async" />
          <button aria-label="Next" onClick={onNext} className="hidden md:flex items-center justify-center w-12 h-12 text-white/80 hover:text-white">›</button>
        </div>

        <div className="mt-3 text-center text-white/70">
          <span>{index + 1} / {images.length}</span>
        </div>
      </div>
    </div>
  );
}